import { useCallback, useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { LikedPropertyIds } from 'components/marketplace/types';

import { MarketplaceViewerFavoritesDocument } from './__generated__/marketplace-favorites.zeus';
import type { MarketplaceFavoriteChangeHandler } from './useMarketplaceFavorite';

const viewerFavoritesQueryKey = (viewerId: string | null | undefined) =>
  ['marketplace', 'viewer-favorites', viewerId] as const;

export const useViewerMarketplaceFavorites = (
  viewerId: string | null | undefined,
  enabled: boolean,
) => {
  const queryClient = useQueryClient();
  const shouldFetch = enabled && Boolean(viewerId);
  const { data, error } = useQuery({
    queryKey: viewerFavoritesQueryKey(viewerId),
    queryFn: async ({ signal }): Promise<LikedPropertyIds> => {
      const result = await requestZeusFromBrowser(MarketplaceViewerFavoritesDocument, {}, signal);
      return new Set(result.customerAccount.buyer.favorites.map((favorite) => favorite.addressId));
    },
    enabled: shouldFetch,
    retry: 1,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-browse', operation: 'viewerFavorites' },
      });
    }
  }, [error]);

  const onFavoriteChange = useCallback<MarketplaceFavoriteChangeHandler>(
    async (addressId, isLiked) => {
      if (!viewerId) {
        return;
      }
      const queryKey = viewerFavoritesQueryKey(viewerId);
      await queryClient.cancelQueries({ queryKey, exact: true });
      const favorites = queryClient.getQueryData<LikedPropertyIds>(queryKey);
      if (!favorites) {
        await queryClient.resetQueries({ queryKey, exact: true });
        return;
      }
      queryClient.setQueryData<LikedPropertyIds>(queryKey, () => {
        if (favorites.has(addressId) === isLiked) {
          return favorites;
        }
        const updatedFavorites = new Set(favorites);
        if (isLiked) {
          updatedFavorites.add(addressId);
        } else {
          updatedFavorites.delete(addressId);
        }
        return updatedFavorites;
      });
    },
    [queryClient, viewerId],
  );

  return {
    likedPropertyIds: data,
    initialFavoritesPending: shouldFetch && data === undefined,
    onFavoriteChange,
  };
};
